// APIリクエストキャッシュシステム
// 同一URLの重複リクエストを防止し、パフォーマンスを向上させる

interface CacheEntry {
  data: any;
  timestamp: number;
  expiry: number;
}

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

class RequestCache {
  private cache = new Map<string, CacheEntry>();
  private pendingRequests = new Map<string, PendingRequest>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5分

  /**
   * キャッシュされたデータを取得
   * @param key キャッシュキー
   * @returns キャッシュされたデータまたはnull
   */
  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }

    // 期限切れチェック
    if (Date.now() > entry.expiry) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * データをキャッシュに保存
   * @param key キャッシュキー
   * @param data 保存するデータ
   * @param ttl キャッシュ保持時間（ミリ秒、デフォルト5分）
   */
  set(key: string, data: any, ttl = this.DEFAULT_TTL): void {
    const now = Date.now();
    this.cache.set(key, {
      data,
      timestamp: now,
      expiry: now + ttl,
    });
  }

  /**
   * 進行中のリクエストを取得
   * @param key リクエストキー
   * @returns 進行中のPromiseまたはnull
   */
  getPendingRequest(key: string): Promise<any> | null {
    const pending = this.pendingRequests.get(key);
    if (!pending) {
      return null;
    }

    // 30秒以上経過したリクエストは削除
    if (Date.now() - pending.timestamp > 30000) {
      this.pendingRequests.delete(key);
      return null;
    }

    return pending.promise;
  }

  /**
   * 進行中のリクエストを登録
   * @param key リクエストキー
   * @param promise リクエストのPromise
   */
  setPendingRequest(key: string, promise: Promise<any>): void {
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now(),
    });

    // リクエスト完了後にpendingから削除
    promise.finally(() => {
      this.pendingRequests.delete(key);
    });
  }

  /**
   * キャッシュをクリア
   * @param keyPrefix 特定のプレフィックスのキーのみクリア（省略時は全削除）
   */
  clear(keyPrefix?: string): void {
    if (keyPrefix) {
      const keysToDelete: string[] = [];
      this.cache.forEach((_, key) => {
        if (key.startsWith(keyPrefix)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      this.cache.clear();
    }
  }

  /**
   * 期限切れのキャッシュエントリを削除
   */
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.cache.forEach((entry, key) => {
      if (now > entry.expiry) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * キャッシュサイズを取得
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * キャッシュの統計情報を取得
   */
  getStats(): {
    cacheSize: number;
    pendingRequests: number;
    oldestEntry?: number;
    newestEntry?: number;
  } {
    const entries = Array.from(this.cache.values());
    const timestamps = entries.map(entry => entry.timestamp);

    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      oldestEntry: timestamps.length > 0 ? Math.min(...timestamps) : undefined,
      newestEntry: timestamps.length > 0 ? Math.max(...timestamps) : undefined,
    };
  }
}

// シングルトンインスタンス
export const requestCache = new RequestCache();

// 定期的なクリーンアップ（10分おき）
setInterval(
  () => {
    requestCache.cleanup();
  },
  10 * 60 * 1000
);

// デバッグ用にwindowオブジェクトに追加（開発環境のみ）
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).__requestCache = requestCache;
}
